import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Sparkles, Search, PenLine, Kanban, Calendar,
  ShieldCheck, Zap, TrendingUp, Building2, GraduationCap,
  Church, Users, HandHeart
} from "lucide-react";
import { Button } from "@/components/ui/button";
import MarketingNav from "@/components/MarketingNav";

const STATS = [
  { n: "$2.3B+", label: "Grants tracked" },
  { n: "12,400+", label: "Live opportunities" },
  { n: "94%", label: "Faster drafting" },
  { n: "8×", label: "More applications" },
];

const featureGridClass = (i) => {
  if (i === 0 || i === 5) return "md:col-span-4";
  return "md:col-span-2";
};

const FEATURES = [
  {
    icon: Search,
    title: "Discover, matched.",
    desc: "Continuously scan federal, foundation, corporate & municipal sources. Only the grants that fit — surfaced with a fit score.",
  },
  {
    icon: PenLine,
    title: "AI Grant Writer.",
    desc: "Claude Sonnet 4.5 drafts narrative, budget justification, and impact sections tailored to each RFP.",
  },
  {
    icon: Kanban,
    title: "Application tracker.",
    desc: "A quiet Kanban for the loud work. Draft → Review → Submitted → Awarded. Never miss a step.",
  },
  {
    icon: Calendar,
    title: "Deadline calendar.",
    desc: "Every close date pulled onto one calendar. Reminders in your inbox 14, 7, and 2 days before.",
  },
  {
    icon: ShieldCheck,
    title: "Document vault.",
    desc: "One home for 501(c)(3) letters, budgets, logic models, and letters of support. Reused across applications.",
  },
  {
    icon: TrendingUp,
    title: "Reporting.",
    desc: "See the pipeline in dollars — pending, likely, awarded — and export for your board.",
  },
];

const WHO = [
  { icon: HandHeart, label: "Nonprofits" },
  { icon: GraduationCap, label: "Schools" },
  { icon: Church, label: "Churches" },
  { icon: Building2, label: "Businesses" },
  { icon: Users, label: "Municipalities / Government" },
  { icon: Sparkles, label: "Individuals" },
];

const TESTIMONIALS = [
  {
    quote: "We applied to 14 grants last quarter. Before Beacon of Grants that would have taken a full-time hire.",
    name: "Amara Okafor",
    role: "Executive Director, Bridge Community Trust",
    img: "https://images.unsplash.com/photo-1662850886700-4ec19bd30d11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwcHJvZmVzc2lvbmFsJTIwcG9ydHJhaXQlMjBzbWlsaW5nfGVufDB8fHx8MTc4MzQ4MDcwN3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    quote: "The AI writer sounds like our voice, not a template. We landed a $180K NEA grant on the first try.",
    name: "David Chen",
    role: "Development Lead, Prospect Arts Council",
    img: "https://images.unsplash.com/photo-1507152832244-10d45c7eda57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwcHJvZmVzc2lvbmFsJTIwcG9ydHJhaXQlMjBzbWlsaW5nfGVufDB8fHx8MTc4MzQ4MDcwN3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    quote: "A tiny municipal team, running the pipeline of a mid-size city. Beacon of Grants is the reason.",
    name: "Priya Ramanathan",
    role: "Grants Manager, Town of Fairhaven",
    img: "https://images.unsplash.com/photo-1484863137850-59afcfe05386?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwcHJvZmVzc2lvbmFsJTIwcG9ydHJhaXQlMjBzbWlsaW5nfGVufDB8fHx8MTc4MzQ4MDcwN3ww&ixlib=rb-4.1.0&q=85",
  },
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="landing-root">
      <MarketingNav />

      {/* Hero — asymmetric */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 animate-fade-in-up">
            <div className="label-eyebrow mb-6" data-testid="hero-eyebrow">Grant discovery, reimagined</div>
            <h1 className="font-heading font-light text-[#1A1A1A] text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tighter">
              Find funding.<br/>
              <span className="text-[#1C3F35]">Write faster.</span><br/>
              <span className="italic text-[#C1654B]">Win more.</span>
            </h1>
            <p className="mt-8 text-lg leading-relaxed text-[#5E5E5E] max-w-xl">
              Beacon of Grants scans thousands of federal, foundation and corporate grants, matches them to your mission, and drafts the application with a grant writer trained on winning proposals.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button
                onClick={() => navigate("/signup")}
                data-testid="hero-cta-primary"
                className="bg-[#1C3F35] text-white hover:bg-[#1A362D] rounded-md px-6 py-6 text-base"
              >
                Start free — no card <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Link
                to="/pricing"
                data-testid="hero-cta-secondary"
                className="text-[#1A1A1A] hover:text-[#1C3F35] text-sm underline underline-offset-4 decoration-[#7A8C77]"
              >
                See pricing
              </Link>
            </div>

            {/* Who is this for */}
            <div className="mt-14 grid grid-cols-3 sm:grid-cols-6 gap-4">
              {WHO.map(({ icon: Icon, label }) => (
                <div key={label} className="flex flex-col items-center gap-2 text-center" data-testid={`persona-${label.toLowerCase().replace(/\s|\//g, '-')}`}>
                  <div className="w-11 h-11 rounded-md border border-[#E6E4DF] bg-white flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#1C3F35]" strokeWidth={1.75}/>
                  </div>
                  <div className="text-xs text-[#5E5E5E]">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right-side visual: a "control-room" preview card */}
          <div className="lg:col-span-5 relative animate-fade-in-up" style={{ animationDelay: "120ms" }}>
            <div className="relative rounded-lg border border-[#E6E4DF] bg-white shadow-sm overflow-hidden">
              <div
                className="h-40 bg-cover bg-center relative"
                style={{ backgroundImage: `url('https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb21tdW5pdHklMjBtZWV0aW5nJTIwY29sbGFib3JhdGlvbnxlbnwwfHx8fDE3ODM0ODA3MDd8MA&ixlib=rb-4.1.0&q=85')` }}
              >
                <div className="absolute inset-0 bg-[#1C3F35]/25"/>
                <div className="absolute bottom-3 left-4 text-white text-xs uppercase tracking-widest">Live pipeline</div>
              </div>
              <div className="p-6 space-y-4">
                {[
                  { title: "Community Development Block Grant", agency: "HUD", amt: "$500,000", d: "Due in 12 days", tone: "brand" },
                  { title: "Rural Health Innovation Program", agency: "HRSA", amt: "$250,000", d: "Draft in review", tone: "moss" },
                  { title: "NEA Arts Project Grant", agency: "NEA", amt: "$100,000", d: "Submitted", tone: "terracotta" },
                ].map((r) => (
                  <div key={r.title} className="flex items-start justify-between gap-4 pb-4 border-b border-[#E6E4DF] last:border-0 last:pb-0">
                    <div>
                      <div className="text-sm font-medium text-[#1A1A1A]">{r.title}</div>
                      <div className="text-xs text-[#5E5E5E] mt-1">{r.agency} · {r.d}</div>
                    </div>
                    <div className="text-sm font-heading text-[#1C3F35]">{r.amt}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute -z-10 -top-8 -right-8 w-64 h-64 rounded-full bg-[#C1654B]/10 blur-3xl"/>
            <div className="absolute -z-10 -bottom-8 -left-8 w-48 h-48 rounded-full bg-[#7A8C77]/15 blur-2xl"/>
          </div>
        </div>

        {/* Stats bar */}
        <div className="border-t border-b border-[#E6E4DF] bg-white/50">
          <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-[#E6E4DF]">
            {STATS.map((s) => (
              <div key={s.label} className="px-6 md:px-10 py-8 text-center" data-testid={`stat-${s.label.replace(/\s+/g,'-').toLowerCase()}`}>
                <div className="font-heading text-3xl md:text-4xl text-[#1C3F35] font-medium">{s.n}</div>
                <div className="mt-1 label-eyebrow">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features — tetris grid */}
      <section id="features" className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-4">
              <div className="label-eyebrow mb-4">The platform</div>
              <h2 className="font-heading font-light text-4xl lg:text-5xl tracking-tighter leading-tight text-[#1A1A1A]">
                Everything a grants team runs on — in one place.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 text-[#5E5E5E] text-lg leading-relaxed">
              We built Beacon of Grants the way modern development shops actually work. Six modules, no context-switching, no spreadsheets held together with hope.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            {FEATURES.map((f, i) => (
              <article
                key={f.title}
                data-testid={`feature-${i}`}
                className={`bg-white border border-[#E6E4DF] rounded-lg p-8 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 ${featureGridClass(i)}`}
              >
                <div className="w-10 h-10 rounded-md bg-[#1C3F35] flex items-center justify-center">
                  <f.icon className="w-5 h-5 text-white" strokeWidth={1.75}/>
                </div>
                <h3 className="font-heading text-2xl mt-6 mb-2 text-[#1A1A1A] font-medium tracking-tight">{f.title}</h3>
                <p className="text-[#5E5E5E] leading-relaxed">{f.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-24 bg-[#1C3F35] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="label-eyebrow text-white/60 mb-4">How it works</div>
            <h2 className="font-heading font-light text-4xl lg:text-5xl tracking-tighter leading-tight">
              Three steps.<br/>Not eleven.
            </h2>
          </div>
          <ol className="lg:col-span-8 space-y-8 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-8">
            {[
              { n: "01", t: "Tell us your mission", d: "Two minutes. Organization type, focus areas, geography, budget size." },
              { n: "02", t: "See matched grants", d: "Ranked by fit, funding range, and days-to-deadline. Save the ones you want to pursue." },
              { n: "03", t: "Draft with AI", d: "Claude Sonnet 4.5 writes to the RFP. You edit. Track through award." },
            ].map((s) => (
              <li key={s.n} className="border-t border-white/20 pt-6" data-testid={`how-step-${s.n}`}>
                <div className="font-mono text-sm text-white/50">{s.n}</div>
                <div className="font-heading text-2xl mt-3 mb-2">{s.t}</div>
                <p className="text-white/70 leading-relaxed">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="label-eyebrow mb-4">Customers</div>
          <h2 className="font-heading font-light text-4xl lg:text-5xl tracking-tighter leading-tight text-[#1A1A1A] max-w-3xl">
            Teams that turned funding from a scramble into a system.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {TESTIMONIALS.map((t, i) => (
              <figure key={t.name} data-testid={`testimonial-${i}`} className="bg-white border border-[#E6E4DF] rounded-lg p-8 flex flex-col">
                <Zap className="w-4 h-4 text-[#C1654B]"/>
                <blockquote className="mt-4 text-[#1A1A1A] leading-relaxed">"{t.quote}"</blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover"/>
                  <div>
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-xs text-[#5E5E5E]">{t.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="rounded-lg bg-[#F0EDE6] border border-[#E6E4DF] p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="label-eyebrow mb-3">Get started free</div>
              <h2 className="font-heading font-light text-4xl md:text-5xl tracking-tighter text-[#1A1A1A]">
                Your first grant is on us.
              </h2>
              <p className="mt-4 text-[#5E5E5E] text-lg max-w-2xl leading-relaxed">
                Sign up in 30 seconds, get matched to grants that fit, and draft your first application with AI — start with a 7-day free trial, then $29/mo.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Button
                onClick={() => navigate("/signup")}
                data-testid="footer-cta-signup"
                className="bg-[#1C3F35] text-white hover:bg-[#1A362D] rounded-md px-8 py-6 text-base"
              >
                Create your account <ArrowRight className="w-4 h-4 ml-2"/>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E6E4DF] py-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-wrap items-center justify-between gap-4 text-sm text-[#5E5E5E]">
          <div>© 2026 Beacon of Grants · Grant data from Grants.gov & partner sources</div>
          <div className="flex gap-6 flex-wrap">
            <Link to="/pricing" className="hover:text-[#1C3F35]">Pricing</Link>
            <Link to="/refund-policy" className="hover:text-[#1C3F35]" data-testid="footer-refund-link">Refund &amp; Cancellation</Link>
            <a href="mailto:Contact@BeaconofGrants.org" className="hover:text-[#1C3F35]">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
